import type { PersonRequestSchemaType } from "@/components/Forms/PersonForm.tsx";
import { useFetchCompaniesForAutocomplete } from "@/queries/useFetchCompaniesForAutocomplete.ts";
import { CircularProgress, Typography } from "@mui/material";
import { RhfAutocomplete } from "mui-rhf-integration";
import type React from "react";
import { useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

type Option = {
    id: string | null;
    name: string | null;
};

type Props = {
    form: UseFormReturn<PersonRequestSchemaType>;
    referralRadio: string;
};

const PersonReferredByCompanyAutocomplete = ({ form, referralRadio }: Props) => {
    const [searchString, setSearchString] = useState("");
    const formValue = form.watch("idSource");

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchCompaniesForAutocomplete({
        searchString: searchString,
        fieldIdValue: formValue ?? null,
    });

    const options: Option[] = useMemo(() => {
        if (data) {
            return data.map((company) => ({
                id: company.idUUID,
                name: company.name ?? "",
            }));
        }
        return [];
    }, [data]);

    const handleInputChange = async (_event: React.SyntheticEvent, newInputValue: string) => {
        if (newInputValue.length >= 2) {
            setSearchString(newInputValue);
        }
    };

    return (
        <>
            {(isLoading || isRefetching) && <CircularProgress />}
            {isError && <Typography variant={"h5"}>Error loading companies</Typography>}
            <RhfAutocomplete
                control={form.control}
                name="idSource"
                options={options}
                loading={isLoading || isRefetching}
                onInputChange={handleInputChange}
                textFieldProps={{
                    label: "Source",
                }}
                // display the name attribute as the dropdown value
                getOptionLabel={(option: Option | string) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    return option.name || "";
                }}
                getOptionKey={(option: Option | string) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    return option.id || "";
                }}
                optionToValue={(option: Option | string) => {
                    if (typeof option === "string") {
                        return option;
                    }

                    return option.id || "";
                }}
                valueToOption={(value) => {
                    return options.find((option) => option.id === value);
                }}
                sx={{
                    display: referralRadio === "Company" ? "block" : "none",
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
        </>
    );
};

export default PersonReferredByCompanyAutocomplete;
